import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Bot, Brain, ChevronRight, FileText, FolderSearch, Globe, Image as ImageIcon, ListChecks, NotebookPen, PencilLine, Plug, SquareTerminal, Wrench, } from 'lucide-react';
import { useId, useState } from 'react';
import { fileStats } from '../lib/diff.js';
import { formatDuration } from '../lib/format.js';
import { parseCommand, searchCounts, summarizePaths } from '../lib/summarize.js';
import { effectiveState } from '../model.js';
import { ArtifactImage } from './ArtifactPresentation.js';
import { CodeBlock, CopyButton } from './CodeBlock.js';
import { DiffView } from './DiffView.js';
import { OutputBlock } from './OutputBlock.js';
import { cx, DiffStat, STATE_LABEL, StatusGlyph, useViewer } from './primitives.js';
import { RefLink } from './RefLink.js';
export const TOOL_ICON = {
    shell: SquareTerminal,
    edit: PencilLine,
    write: NotebookPen,
    read: FileText,
    search: FolderSearch,
    web: Globe,
    mcp: Plug,
    subagent: Bot,
    todo: ListChecks,
    plan: ListChecks,
    image_gen: ImageIcon,
    question: Wrench,
    other: Wrench,
};
const VERB = {
    shell: ['Running', 'Ran'],
    edit: ['Editing', 'Edited'],
    write: ['Writing', 'Wrote'],
    read: ['Reading', 'Read'],
    search: ['Searching', 'Searched'],
    web: ['Fetching', 'Fetched'],
    mcp: ['Calling', 'Called'],
    subagent: ['Delegating', 'Delegated'],
    todo: ['Updating todos', 'Updated todos'],
    plan: ['Planning', 'Planned'],
    image_gen: ['Generating image', 'Generated image'],
    question: ['Asking', 'Asked'],
    other: ['Using', 'Used'],
};
const GROUP_NOUN = {
    read: ['file', 'files'],
    search: ['search', 'searches'],
    web: ['page', 'pages'],
    mcp: ['tool call', 'tool calls'],
};
export function toolVerb(t) {
    const [live, done] = VERB[t.toolKind];
    return t.state === 'streaming' || t.state === 'pending' ? live : done;
}
/** Paths under the session cwd render relative; long absolute paths keep their tail. */
export function displayPath(p, cwd) {
    if (cwd) {
        const base = cwd.endsWith('/') || cwd.endsWith('\\') ? cwd : `${cwd}/`;
        if (p.startsWith(base))
            return p.slice(base.length);
    }
    const parts = p.split(/[\\/]/);
    return parts.length > 4 && p.startsWith('/') ? `…/${parts.slice(-3).join('/')}` : p;
}
export function toolTarget(t, cwd) {
    if (t.toolKind === 'shell') {
        const c = parseCommand(t.command ?? t.title, cwd);
        return { text: c.title, mono: true, ...(c.dir ? { dir: c.dir } : {}), env: c.env };
    }
    if (t.toolKind === 'mcp' && t.mcp)
        return { text: `${t.mcp.server} · ${t.mcp.tool}`, mono: false };
    const p = t.path && (t.toolKind === 'read' || t.toolKind === 'edit' || t.toolKind === 'write')
        ? t.path
        : t.title.startsWith('/')
            ? t.title
            : undefined;
    if (p) {
        // File name first (it's what you scan for), directory dimmed after it.
        const rel = displayPath(p, cwd);
        const parts = rel.split(/[\\/]/);
        const name = parts.pop() ?? rel;
        return { text: name, mono: true, dir: parts.join('/') || undefined };
    }
    return { text: t.title, mono: false };
}
/** Compact chip form of a tool call (grouped activity, inbox context, subagent status). */
export function ToolChip({ tool, onClick }) {
    const { connection, cwd } = useViewer();
    const state = effectiveState(tool.state, connection);
    const Icon = TOOL_ICON[tool.toolKind];
    const target = toolTarget(tool, cwd);
    const body = (_jsxs(_Fragment, { children: [_jsx(Icon, { size: 12, "aria-hidden": "true" }), _jsx("span", { className: cx('vk-toolchip__text', target.mono && 'vk-mono'), children: target.text }), state !== 'ok' ? _jsx(StatusGlyph, { state: state, size: 11, label: STATE_LABEL[state] }) : null] }));
    return onClick ? (_jsx("button", { type: "button", className: cx('vk-toolchip', `vk-toolchip--${state}`), onClick: onClick, title: tool.title, children: body })) : (_jsx("span", { className: cx('vk-toolchip', `vk-toolchip--${state}`), title: tool.title, children: body }));
}
/**
 * One line of activity. Collapsed at L1; expanded (defaultOpen) at L2. The whole row is the
 * disclosure button (≥44px on mobile, 32px compact).
 */
export function ActivityRow({ icon: Icon, verb, target, mono, meta, state, defaultOpen = false, children, tone, className, }) {
    const { connection } = useViewer();
    const eff = effectiveState(state, connection);
    const [open, setOpen] = useState(defaultOpen);
    const id = useId();
    const expandable = children != null;
    const content = (_jsxs(_Fragment, { children: [_jsx("span", { className: cx('vk-act__icon', tone && `vk-tone-${tone}`), "aria-hidden": "true", children: _jsx(Icon, { size: 15, strokeWidth: 1.9 }) }), _jsxs("span", { className: "vk-act__label", children: [_jsx("span", { className: "vk-act__verb", children: verb }), target ? _jsx("span", { className: cx('vk-act__target', mono && 'vk-mono'), children: target }) : null] }), _jsx("span", { className: "vk-act__meta", children: meta }), _jsx("span", { className: "vk-act__status", children: eff === 'ok' ? null : _jsx(StatusGlyph, { state: eff, label: STATE_LABEL[eff] }) }), expandable ? (_jsx(ChevronRight, { size: 14, className: cx('vk-chev vk-act__chev', open && 'is-open'), "aria-hidden": "true" })) : (_jsx("span", { className: "vk-act__chev" }))] }));
    return (_jsxs("div", { className: cx('vk-act', `vk-act--${eff}`, open && 'is-open', className), children: [expandable ? (_jsx("button", { type: "button", className: "vk-act__row", "aria-expanded": open, "aria-controls": id, onClick: () => setOpen((o) => !o), children: content })) : (_jsx("div", { className: "vk-act__row", children: content })), expandable && open ? (_jsx("div", { className: "vk-act__detail", id: id, children: children })) : null] }));
}
function jsonPreview(v) {
    try {
        return JSON.stringify(v, null, 2);
    }
    catch {
        return String(v);
    }
}
export function ToolDetail({ tool }) {
    const { cwd, refs } = useViewer();
    const out = tool.result?.output ?? tool.output;
    const blocks = [];
    // openable-refs: the file a read/edit/write touched opens in the inspector.
    if (refs &&
        tool.path &&
        (tool.toolKind === 'read' || tool.toolKind === 'edit' || tool.toolKind === 'write'))
        blocks.push(_jsx("p", { className: "vk-meta vk-toolref", children: _jsx(RefLink, { refValue: { kind: 'path', text: tool.path, strength: 'strong' }, children: _jsx("span", { className: "vk-mono", children: displayPath(tool.path, cwd) }) }) }, "ref"));
    if (tool.toolKind === 'shell' && tool.command)
        blocks.push(_jsx(CommandFull, { tool: tool, cwd: tool.cwd ?? cwd }, "cmd"));
    if (tool.diffs.length) {
        blocks.push(_jsx(DiffView, { files: tool.diffs, maxRowsPerFile: 24 }, "diff"));
    }
    if (tool.toolKind === 'mcp' || tool.toolKind === 'other') {
        if (tool.input != null)
            blocks.push(_jsx(CodeBlock, { code: jsonPreview(tool.input), lang: "json", title: "Input" }, "in"));
    }
    if (tool.images.length) {
        blocks.push(_jsx("div", { className: "vk-thumbs", children: tool.images.map((im) => (_jsx(ArtifactImage, { image: im, className: "vk-thumb-img" }, im.artifactId ?? im.url ?? im.path))) }, "img"));
    }
    if (out && !(tool.diffs.length && tool.toolKind !== 'shell')) {
        const looksJson = /^\s*[[{]/.test(out) && tool.toolKind === 'mcp';
        blocks.push(looksJson ? (_jsx(CodeBlock, { code: out, lang: "json", title: "Result" }, "out")) : (_jsx(OutputBlock, { text: out, title: tool.toolKind === 'shell'
                ? 'Command output'
                : `${toolVerb(tool)} ${toolTarget(tool, cwd).text}`, command: tool.command, terminal: tool.toolKind === 'shell', state: tool.state, exitCode: tool.result?.exitCode, head: tool.toolKind === 'read' ? 8 : 6, tail: tool.toolKind === 'read' ? 0 : 4 }, "out")));
    }
    else if (!out && tool.toolKind === 'shell') {
        blocks.push(_jsx(OutputBlock, { text: "", state: tool.state }, "out"));
    }
    if (tool.hooks.length) {
        blocks.push(_jsx("div", { className: "vk-hooks", children: tool.hooks.map((h) => (_jsxs("div", { className: "vk-hook", children: [_jsx(StatusGlyph, { state: h.state, size: 12, label: STATE_LABEL[h.state] }), _jsxs("span", { className: "vk-meta", children: ["hook ", h.hookEvent, h.hookName ? ` · ${h.hookName}` : ''] }), h.output ? _jsx("span", { className: "vk-mono vk-trunc", children: h.output }) : null] }, h.id))) }, "hooks"));
    }
    return _jsx("div", { className: "vk-stack", children: blocks });
}
export function ToolRow({ tool, defaultOpen }) {
    const { cwd } = useViewer();
    const target = toolTarget(tool, cwd);
    const stats = tool.diffs.reduce((a, d) => {
        const s = fileStats(d);
        return { additions: a.additions + s.additions, deletions: a.deletions + s.deletions };
    }, { additions: 0, deletions: 0 });
    const shell = tool.toolKind === 'shell';
    // A denied call never ran: no exit status to show.
    const exit = tool.result?.status === 'denied' ? undefined : tool.result?.exitCode;
    const meta = (_jsxs(_Fragment, { children: [target.env?.map((e) => (_jsx("span", { className: "vk-badge vk-mono", children: e }, e))), tool.background ? _jsx("span", { className: "vk-badge", children: "background" }) : null, tool.approval?.decision === 'allow_always' ? (_jsx("span", { className: "vk-badge", children: "always allowed" })) : null, tool.toolKind === 'search' && searchSummary(tool) ? (_jsx("span", { children: searchSummary(tool) })) : null, tool.progress && tool.state === 'streaming' ? (_jsx("span", { className: "vk-trunc", children: tool.progress })) : null, _jsx(DiffStat, { ...stats }), tool.durationMs != null && (tool.durationMs >= 1000 || shell) ? (_jsx("span", { children: tool.durationMs < 1000 ? '<1s' : formatDuration(tool.durationMs) })) : null, exit != null && (shell || exit !== 0) ? (exit === 0 ? (_jsx("span", { className: "vk-act__exit vk-tone-ok", role: "img", "aria-label": "exit 0", children: "\u2713" })) : (_jsxs("span", { className: "vk-act__exit vk-tone-fail", children: ["\u2717 exit ", exit] }))) : null] }));
    return (_jsx(ActivityRow, { icon: TOOL_ICON[tool.toolKind], verb: toolVerb(tool), target: target.dir ? (shell ? (_jsxs(_Fragment, { children: [_jsx("span", { className: "vk-act__dir vk-act__dir--badge", children: target.dir }), " ", target.text] })) : (_jsxs(_Fragment, { children: [target.text, " ", _jsx("span", { className: "vk-act__dir", children: target.dir })] }))) : (target.text), mono: target.mono, meta: meta, state: tool.state, defaultOpen: defaultOpen, children: _jsx(ToolDetail, { tool: tool }) }));
}
export function GroupRow({ group, defaultOpen }) {
    const { cwd } = useViewer();
    const noun = GROUP_NOUN[group.toolKind] ?? ['call', 'calls'];
    const live = group.state === 'streaming';
    const verb = VERB[group.toolKind][live ? 0 : 1];
    const n = group.items.length;
    const paths = group.items
        .map((t) => t.path ?? (t.title.startsWith('/') ? t.title : ''))
        .filter(Boolean);
    const files = group.toolKind === 'read' && paths.length === n ? summarizePaths(paths, cwd) : null;
    return (_jsx(ActivityRow, { icon: TOOL_ICON[group.toolKind], verb: files
            ? `${verb} ${files.count} ${files.count === 1 ? noun[0] : noun[1]}${files.where ? ` ${files.where}` : ''}`
            : `${verb} ${n} ${n === 1 ? noun[0] : noun[1]}`, target: _jsx("span", { className: "vk-act__names", children: files
                ? files.names
                : group.items
                    .slice(0, 3)
                    .map((t) => toolTarget(t, cwd).text.split(/[\\/]/).pop())
                    .join(', ') + (n > 3 ? `, +${n - 3}` : '') }), state: group.state, defaultOpen: defaultOpen, children: _jsx("div", { className: "vk-stack vk-stack--tight", children: group.items.map((t) => (_jsx(ToolRow, { tool: t }, t.id))) }) }));
}
export function ThinkingRow({ text, streaming, durationMs, redacted, defaultOpen, }) {
    return (_jsx(ActivityRow, { icon: Brain, verb: streaming ? 'Thinking…' : `Thought${durationMs ? ` for ${formatDuration(durationMs)}` : ''}`, target: streaming ? _jsx("span", { className: "vk-act__preview", children: text.slice(-80) }) : undefined, state: streaming ? 'streaming' : 'ok', defaultOpen: defaultOpen, className: "vk-act--thinking", children: _jsx("div", { className: "vk-thinking", children: redacted ? _jsx("em", { children: "Thinking content is redacted by the provider." }) : text }) }));
}
/** The exact command (wrapped, copyable) with where and how it ran. */
export function CommandFull({ tool, cwd }) {
    const full = parseCommand(tool.command ?? '', cwd).full;
    const exit = tool.result?.exitCode;
    return (_jsxs("div", { className: "vk-cmdfull", children: [_jsxs("div", { className: "vk-cmdfull__head", children: [_jsx("span", { className: "vk-meta", children: "Full command" }), _jsx("span", { className: "vk-grow" }), _jsx(CopyButton, { text: full, label: "Copy command" })] }), _jsx("pre", { className: "vk-cmdfull__pre vk-mono", children: full }), _jsxs("dl", { className: "vk-cmdfull__facts", children: [cwd ? (_jsxs("div", { children: [_jsx("dt", { children: "Directory" }), _jsx("dd", { className: "vk-mono", children: cwd })] })) : null, _jsxs("div", { children: [_jsx("dt", { children: "Exit" }), _jsx("dd", { children: exit ?? (tool.state === 'streaming' ? 'running' : 'unknown') })] }), tool.durationMs != null ? (_jsxs("div", { children: [_jsx("dt", { children: "Duration" }), _jsx("dd", { children: tool.durationMs < 1000
                                    ? `${Math.round(tool.durationMs)}ms`
                                    : formatDuration(tool.durationMs) })] })) : null] })] }));
}
/** Search rows: "(6 matches in 4 files)" from the tool output, when the output says. */
export function searchSummary(tool) {
    const c = searchCounts(tool.result?.output ?? tool.output);
    if (c.matches != null && c.files != null)
        return `${c.matches} matches in ${c.files} files`;
    if (c.files != null)
        return `${c.files} files`;
    return undefined;
}
//# sourceMappingURL=ActivityRow.js.map